import {
  Component,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  signal,
  WritableSignal,
  inject,
  computed,
  effect,
  ChangeDetectionStrategy,
} from '@angular/core'
import { Router } from '@angular/router'
import { CoreService, NotificationService } from '../../services'
import { Product, File, User } from 'depoto-core/src/entities'
import { BaseListing } from '../_base-listing/base-listing'
import { ExportsComponent } from '../exports/exports.component'
import { ListingItem } from 'depoto-core/src/models'
import { productsColumns } from '../../utils/definitions.util'
import {
  ModalProductPurchaseComponent,
  PurchaseOrderStrategy,
} from './components/modal-product-purchase/modal-product-purchase.component'
import { TranslateService } from '@ngx-translate/core'
import { DepotService } from '../../services/entity-services/depot.service'
import { PaymentService } from '../../services/entity-services/payment.service'
import { ProducerService } from '../../services/entity-services/producer.service'
import { VatService } from '../../services/entity-services/vat.services'
import { ProductService } from '../../services/entity-services/product.service'
import { SupplierService } from '../../services/entity-services/supplier.services'
import { injectInfiniteQuery, injectQuery } from '@tanstack/angular-query-experimental'
import { TagService } from '../../services/entity-services/tag.service'
import { createSubmittedFilterState } from '../../utils'

type StorageKey = { type: string; key: string }
type QuantityLevelField = 'quantityMinimum' | 'quantityOptimum' | 'quantityMaximum'
type ExcludedPurchaseOrderProduct = {
  id: number
  fullName: string
  code?: string
  ean?: string
  missingLevel: string
}

const DEFAULT_FILTERS = {
  fulltext: '',
  depots: [],
  availability: 'all',
  tags: [],
  tagsNegate: [],
  isBundle: 'all',
  enabled: 'yes',
  code: '',
  ean: '',
  suppliers: [],
  producers: [],
  expirationDateTo: null,
  listType: 'children_and_parents_without_children',
  categories: [],
  metrics: '',
  hasHsCode: 'all',
  position1: '',
  position2: '',
  position3: '',
}
@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProductsComponent extends BaseListing implements OnInit, OnChanges, OnDestroy {
  private productService = inject(ProductService)
  private depotService = inject(DepotService)
  private paymentService = inject(PaymentService)
  private producerService = inject(ProducerService)
  private tagService = inject(TagService)
  private vatService = inject(VatService)
  private supplierService = inject(SupplierService)

  @ViewChild('exportsModal') exportsModal: ExportsComponent
  @ViewChild('modalproductPurchase') modalproductPurchase: ModalProductPurchaseComponent
  listingColumns = signal(productsColumns)
  listTypes: { val: string; name: string }[] = [
    { val: 'parents', name: 'Hlavní produkty' },
    { val: 'parents_with_children', name: 'Hlavní s variantami' },
    { val: 'parents_without_children', name: 'Hlavní bez variant' },
    { val: 'children_and_parents_without_children', name: 'Skladové položky' },
    { val: 'children', name: 'Varianty' },
    { val: 'all', name: 'Vše' },
  ]
  availabilityTypes: string[] = ['all', 'available', 'reservation', 'stock', 'unavailable']
  purchaseOrdersTypes = [
    'all',
    'available_less_than_minimum',
    'available_less_than_optimum',
    'available_more_than_optimum',
    'available_more_than_maximum',
    'unavailable',
  ]
  abilityTypes: string[] = ['all', 'yes', 'no']
  expirations: string[] = ['all', 'monthEnd', 'after1month', 'after3months', 'custom']
  selectedExpiration: string
  // totalItems = computed(() => {
  //   console.error(this.queryProducts.data())
  //   return 100
  // })
  // currentPage: number
  // endPage: number
  // storageKey: WritableSignal<{ type: string; key: string }> = signal({ type: '', key: '' })
  sortProp = signal('name')
  sortDir = signal('asc')
  // todo
  // selectedColumns: WritableSignal<ListingItem[]> = signal(
  //   this.loadViewSettings('products').selectedColumns || this.listingColumns().filter(c => c.isDefault)
  // )
  selectedColumns: WritableSignal<ListingItem[]> = signal(this.listingColumns().filter(c => c.isDefault))
  storageKey = signal<StorageKey>({ type: '', key: '' })

  canCreate = false
  loading = true
  filtersExpanded = signal(false)
  readonly defaultFilters = DEFAULT_FILTERS
  readonly advancedFilterKeys = [
    'isBundle',
    'enabled',
    'code',
    'ean',
    'suppliers',
    'producers',
    'expirationDateTo',
    'listType',
    'categories',
    'metrics',
    'hasHsCode',
    'position1',
    'position2',
    'position3',
  ]

  // Keep as a signal object; never overwrite the signal instance
  filters = signal(structuredClone(DEFAULT_FILTERS))

  readonly queryFilters = computed(() => {
    const filtersWithValues = {}
    for (const [key, value] of Object.entries(this.filters())) {
      // if it has value
      if (
        value !== undefined &&
        value !== null &&
        value !== false &&
        value !== 0 &&
        value !== '' &&
        value !== 'all' &&
        (!Array.isArray(value) || (Array.isArray(value) && value?.length))
      ) {
        if (key === 'depots' && Array.isArray(value)) {
          const depotIds = this.getAllowedDepotFilterIds(value)
          if (depotIds.length) {
            filtersWithValues[key] = depotIds
          }
        } else if (value === 'yes' || value === 'no') {
          // convert tri-state string to boolean
          filtersWithValues[key] = value === 'yes'
        } else if (Array.isArray(value) && value[0]?.id) {
          // map arrays of objects to ids
          filtersWithValues[key] = value.map(object => object?.id)
        } else if ((value as any)?.id) {
          // single object with id (safety)
          filtersWithValues[key] = (value as any).id
        } else {
          // primitives and other values (e.g., code, ean, metrics, listType, expirationDateTo)
          filtersWithValues[key] = value
        }
      }
    }
    // Thresholds and incoming purchases must use the same depot scope as stock.
    if (
      filtersWithValues['depots']?.length &&
      [
        'available_less_than_minimum',
        'available_less_than_optimum',
        'available_more_than_optimum',
        'available_more_than_maximum',
      ].includes(filtersWithValues['metrics'])
    ) {
      filtersWithValues['metricDepots'] = filtersWithValues['depots']
    }
    return filtersWithValues
  })

  keyEventSubscriptions: any = {}

  checkedMap = signal<Record<string, boolean>>({})

  private readonly columnsId = computed(() => (this.selectedColumns() ?? []).map(c => c.name).join(','))
  private readonly submittedFilters = createSubmittedFilterState<typeof DEFAULT_FILTERS, Record<string, any>>({
    saveView: filters => this.saveView(filters),
    refetch: () => this.queryProducts.refetch(),
  })
  private readonly submittedExpiration = signal('all')
  readonly submittedQueryFilters = computed(() => this.submittedFilters.snapshot()?.request ?? {})

  products = computed(() => {
    const pages = this.queryProducts.data()?.pages ?? []
    const map = this.checkedMap()
    // keep shape stable, but avoid heavy remapping work when possible
    const items = pages?.flatMap(p => p?.items)
    return items.map(item => ({ ...item, isChecked: map[item?.id] ?? false }))
  })

  checkedProducts = computed(() => {
    // When "select all" is active, we don't need individual selections
    if (this.areAllProductsChecked()) return []
    const map = this.checkedMap()
    // Filter against raw items to avoid recreating large arrays unnecessarily
    const items = this.queryProducts.data()?.pages?.flatMap(p => p?.items) ?? []
    return items?.filter(p => !!map[p?.id])
  })

  areAllProductsChecked = signal<boolean>(false)
  reloading = true
  isPurchaseOrderAllowed = false
  private collapsedStates: { [key: number]: boolean } = {}

  // Help Angular keep DOM nodes stable and avoid scroll jumps when
  // reactive state updates recreate item objects
  trackByProductId(index: number, item: any): number | string {
    return item?.id ?? index
  }

  readonly queryDepotsOptions = injectQuery(() => ({
    queryKey: ['depots', 'options'],
    queryFn: async () => {
      if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.core.services.user.user?.depots?.length) {
        // TODO CORE USER
        return []
      } else {
        return await this.depotService
          .getDepots(
            {
              sort: 'name',
              direction: 'asc',
            },
            { id: null, name: null, enabled: null }
          )
          .then(result => result.items)
      }
    },
    select: data => {
      if (
        !this.depotService.doesUserHaveAllDepots(data as any[]) &&
        this.core.services.user.user?.depots?.length // TODO CORE USER
      ) {
        return this.core.services.user.user.depots.filter(d => d.enabled) // TODO CORE USER
      } else {
        return (data as any[]).filter(d => d.enabled)
      }
    },
  }))

  readonly queryVats = injectQuery(() => ({
    queryKey: ['vats', 'options', 'v1'],
    queryFn: async () => {
      const res = await this.vatService.getVats({}, { id: null, name: null, percent: null, default: null })
      return res.items
    },
    select: (items: any[]) => items.map(({ id, name, default: isDefault }) => ({ id, name, isDefault })),
    // staleTime: Infinity,
    gcTime: 6 * 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  }))

  readonly queryPayments = injectQuery(() => ({
    queryKey: ['payments', 'options', 'v1'],
    queryFn: async () => {
      const res = await this.paymentService.getPayments({}, { id: null, name: null, type: { id: null } })
      return res.items
    },
    select: (items: any[]) => items.map(({ id, name, type }) => ({ id, name, typeId: type?.id })),
    // staleTime: Infinity,
    gcTime: 6 * 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  }))

  readonly queryProducers = injectQuery(() => ({
    queryKey: ['producers', 'options', 'v1'],
    queryFn: async () => {
      const res = await this.producerService.getProducers({}, { id: null, name: null })
      return res.items
    },
    select: (items: any[]) => items.map(({ id, name }) => ({ id, name })),
    // staleTime: Infinity,
    gcTime: 6 * 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  }))

  totalItems = computed(() => this.queryProducts.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  readonly querySuppliers = injectQuery(() => ({
    queryKey: ['suppliers', 'options', 'v1'],
    queryFn: async () => {
      const res = await this.supplierService.getSuppliers()
      return res.items
    },
    select: (items: any[]) => items.map(({ id, name }) => ({ id, name })),
    // staleTime: Infinity,
    gcTime: 6 * 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  }))

  readonly queryTags = injectQuery(() => ({
    queryKey: ['tags', 'options'],
    queryFn: async () => {
      return await this.tagService
        .getTags({ sort: 'name', direction: 'asc' }, { id: null, name: null, type: null })
        .then(result => result.items)
    },
    // .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })),
    select: data => (data as any[]).filter(tag => tag.type === 'product'),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  }))

  readonly queryProducts = injectInfiniteQuery(() => {
    const snapshot = this.submittedFilters.snapshot()
    const sortProp = this.sortProp()
    const sortDir = this.sortDir()
    const columnsId = this.columnsId()
    const requestFilters = snapshot?.request ?? {}
    const schema = this.setSchema(requestFilters)

    return {
      enabled: this.submittedFilters.ready(),
      queryKey: ['products', 'list', { sortProp, sortDir, columnsId, filters: requestFilters }],
      queryFn: ({ pageParam }) =>
        this.productService.getProducts(
          {
            page: pageParam,
            sort: sortProp,
            direction: sortDir,
            filters: requestFilters,
          },
          schema
        ),
      initialPageParam: 1,
      getNextPageParam: result => {
        return Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
          ? Number(result?.paginator?.current) + 1
          : undefined
      },
    }
  })

  areAllListedProductsChecked = computed(() => {
    return this.products().every(p => p.isChecked === true)
  })

  loadingNextPage = computed(() => this.queryProducts.isFetchingNextPage())
  nextButtonShow = computed(() => this.queryProducts.hasNextPage())

  loadingFirstPage = computed(() => {
    return this.queryProducts.isFetching() && !this.queryProducts.isFetchingNextPage()
  })

  areCoreDataLoading = computed(() => {
    return (
      this.queryProducers.isFetching() ||
      this.queryDepotsOptions.isFetching() ||
      this.queryPayments.isFetching() ||
      this.queryTags.isFetching() ||
      this.queryVats.isFetching() ||
      this.querySuppliers.isFetching()
    )
  })

  constructor(
    private router: Router,
    public core: CoreService,
    public translateService: TranslateService,
    private readonly notificationService: NotificationService
  ) {
    super(core)
    this.storageKey.set(this.keyService.getStorageKeyForEntity('products'))
    this.loadView()
    effect(() => {
      this.queryTags.data()
      this.queryPayments.data()
      this.queryProducers.data()
      const depotOptions = this.queryDepotsOptions.data()
      this.sanitizeDepotFilters(depotOptions)
      this.querySuppliers.data()
      this.queryVats.data()
      this.queryProducts.data()
    })
  }

  async ngOnInit() {
    // this.loadView()
    if (this.core.services.user.user) {
      this.isPurchaseOrderAllowed = this.checkIfPurchaseOrderIsAllowedForCompany(this.core.services.user.user.company)
      this.checkPerms()
    }
    this.core.services.user.currentUserEmitter.subscribe().then((user: User) => {
      this.checkPerms()
      if (user) {
        this.isPurchaseOrderAllowed = this.checkIfPurchaseOrderIsAllowedForCompany(user.company)
      }
    })
    // this.loadSorting('products')
    // await this.filter() todo
    this.keyEventSubscriptions.scannedBarcode = this.core.services.keyEvent.onScannedBarcode.subscribe().then(code => {
      // Use immutable update so signals/computed detect the change
      this.setFilters('ean', code)
      // this.filter() todo
      // this.filtersExpanded = true
      this.filtersExpanded.set(true)
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.storageKey?.currentValue) {
      // this.loadSettings()
    }
  }

  // private checkIfPurchaseOrderIsAllowedForCompany(company: Company | Partial<Company>): boolean { todo
  private checkIfPurchaseOrderIsAllowedForCompany(company: any): boolean {
    return (company as any)?.moduleRelations?.some(
      (moduleRelation: any) => moduleRelation?.module?.id === 'purchase-orders'
    )
  }

  ngOnDestroy() {
    if (this.keyEventSubscriptions.scannedBarcode) {
      this.keyEventSubscriptions.scannedBarcode.unsubscribe()
      this.keyEventSubscriptions.scannedBarcode = null
    }
  }

  createBatchFromFile(res: any[]): void {
    if (res?.length) {
      // this.notificationService.success(this.translateService.instant('products.create.success'))
      // this.filter() todo
    }
  }

  checkPerms() {
    this.canCreate =
      this.core.services.user.hasRole('ROLE_ORDER_CREATE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
  }

  onColumnSettingDropdownClick(event: string) {
    const isSelected = this.selectedColumns().find(c => c.name === event)
    if (isSelected) {
      this.selectedColumns.set(this.selectedColumns().filter(c => c.name !== event))
    } else {
      // this.selectedColumns.update(la => [...this.listingColumns().filter(c => c.name === event), ...la])

      const cols = this.listingColumns().find(c => String(c.name) === String(event))
      const allCols = [...this.selectedColumns(), cols]
      // sorting by reference arr (includes() breaks if isSelected - different objects..):
      this.selectedColumns.set(this.listingColumns().filter(c => allCols.some(aC => aC.name === c.name)))
    }
    // const savedColumns = this.core.services.storage.getSync(this.storageKey()) || {}
    // savedColumns.products = [...this.selectedColumns()]
    // this.core.services.storage.set(this.storageKey(), savedColumns)

    this.saveView()
    // setTimeout(() => this.updateTopScrollState()) todo ?
  }

  isColumnSelected(property) {
    return this.selectedColumns().some(c => c.property === property)
  }

  get productsFromQuery(): Product[] {
    return this.queryProducts.data()?.pages?.flatMap(p => p.items) ?? []
  }

  isProductRowChecked(productId: number): boolean {
    if (this.areAllProductsChecked()) return true
    else {
      return this.checkedProducts().find(checkedProduct => checkedProduct.id === productId) !== undefined
    }
  }

  allProductsChecked(checked: boolean) {
    const mapSignal = this.checkedMap
    if (!mapSignal) return

    const items = this.products() // not itemsWithChecked

    mapSignal.update(map => {
      const newMap = { ...map }
      for (const item of items) {
        newMap[item.id] = checked
      }
      return newMap
    })
  }

  public clearFilters() {
    const sortChanged = this.sortProp() !== 'name' || this.sortDir() !== 'asc'
    this.selectedExpiration = 'all'
    this.filters.set(structuredClone(DEFAULT_FILTERS))
    this.sortProp.set('name')
    this.sortDir.set('asc')
    this.resetSelections()
    this.commitFilters(!sortChanged)
  }

  sortBy(property: string): void {
    // todo opakuje se v hodně componentach - nejde sjednotit na jedno misto ?
    if (this.sortProp() === property) {
      if (this.sortDir() === 'asc') this.sortDir.set('desc')
      else this.sortDir.set('asc')
    } else {
      this.sortProp.set(property)
    }
    // this.currentPage = 1 todo
    // const sorting = { sortProp: this.sortProp, sortDir: this.sortDir }
    // this.core.services.storage.set(this.getStorageKeyForEntity('products'), sorting)
    this.saveView()
    // this.core.services.storage.set(this.getStorageKeyForEntity('products'), sorting)
    // this.filter() todo
  }

  setExpirationFilter(expStr: string = null, expDate: string = null) {
    if (expStr) {
      this.selectedExpiration = expStr
      let date = new Date()
      switch (this.selectedExpiration) {
        case 'all':
          // Ensure immutable update
          this.setFilters('expirationDateTo', null)
          return
        case 'monthEnd':
          date = new Date(date.getFullYear(), date.getMonth() + 1, 0)
          break
        case 'after1month':
          date.setMonth(date.getMonth() + 1)
          break
        case 'after3months':
          date.setMonth(date.getMonth() + 3)
          break
        case 'custom':
          return
      }

      const tzOffset = date.getTimezoneOffset()
      date.setTime(date.getTime() + tzOffset * 60 * -1000)

      this.setFilters('expirationDateTo', `${date.toISOString().substring(0, 10)} 23:59:59`)
    } else if (expDate) {
      this.setFilters('expirationDateTo', `${new Date(expDate).toISOString().substring(0, 10)} 23:59:59`)
    }
  }

  saveView(filters = this.submittedFilters.snapshot()?.view ?? this.filters()): void {
    this.core.services.storage.set(this.storageKey(), {
      selectedColumns: this.selectedColumns().map(c => c.name),
      filters: filters ? JSON.stringify(filters) : undefined,
      selectedExpiration: this.submittedExpiration(),
      sortProp: this.sortProp(),
      sortDir: this.sortDir(),
    })
  }

  loadView(): void {
    const view = this.core.services.storage.getSync(this.storageKey())

    if (view?.selectedColumns?.length) {
      this.selectedColumns.set(
        this.listingColumns().filter(savedColumn => {
          return view?.selectedColumns.find(column => column === savedColumn.name)
        })
      )
    }
    this.filters.set(view?.filters?.length ? JSON.parse(view?.filters) : structuredClone(DEFAULT_FILTERS))
    this.selectedExpiration = view?.selectedExpiration || 'all'
    this.submittedExpiration.set(this.selectedExpiration)
    this.sortProp.set(view?.sortProp || 'name')
    this.sortDir.set(view?.sortDir || 'asc')
    this.submittedFilters.commit(this.filters(), this.queryFilters())
    this.queryProducts.refetch()
    // this.loadSettings()
  }

  private getDepotFilterId(depot: any): any {
    return depot && typeof depot === 'object' ? depot.id : depot
  }

  private getAllowedDepotFilterIds(depots: any[]): any[] {
    const depotOptions = this.queryDepotsOptions.data()
    const depotIds = depots
      .map(depot => this.getDepotFilterId(depot))
      .filter(depotId => depotId !== null && depotId !== undefined)

    if (!Array.isArray(depotOptions)) {
      return depotIds
    }

    const allowedDepotIds = new Set(depotOptions.map(depot => String(depot?.id)))
    return depotIds.filter(depotId => allowedDepotIds.has(String(depotId)))
  }

  private sanitizeDepotFilters(depotOptions: any[] | undefined): void {
    if (!Array.isArray(depotOptions)) {
      return
    }

    const selectedDepots = this.filters().depots
    if (!Array.isArray(selectedDepots) || !selectedDepots.length) {
      return
    }

    const selectedDepotIds = selectedDepots.map(depot => this.getDepotFilterId(depot))
    const allowedDepotIds = this.getAllowedDepotFilterIds(selectedDepots)
    const isSameSelection =
      selectedDepotIds.length === allowedDepotIds.length &&
      selectedDepotIds.every((depotId, index) => String(depotId) === String(allowedDepotIds[index]))

    if (isSameSelection) {
      return
    }

    this.filters.set({
      ...this.filters(),
      depots: allowedDepotIds,
    })
    this.commitFilters(false)
    this.resetSelections()
  }

  // loadSettings(): void {
  //   const savedColumns = this.core.services.storage.getSync(this.storageKey())
  //   if (savedColumns?.products) {
  //     savedColumns.products = savedColumns.products.filter(s => this.listingColumns().find(c => c.name === s.name))
  //     this.selectedColumns.set(savedColumns.products)
  //   }
  // }

  setCheckedProductsToFalse() {
    this.areAllProductsChecked.set(false)
    this.allProductsChecked(false)
  }

  /**
   * Clears any selection state when filters change
   */
  resetSelections() {
    this.areAllProductsChecked.set(false)
    this.checkedMap.set({})
  }

  /**
   * Unified handler for filter submit actions
   */
  onFilterSubmit() {
    this.resetSelections()
    this.commitFilters()
  }

  private commitFilters(refetchUnchanged = true): void {
    this.submittedExpiration.set(this.selectedExpiration)
    this.submittedFilters.submit(this.filters(), this.queryFilters(), { refetchOnUnchanged: refetchUnchanged })
  }

  getThumbUrl(image: File): string {
    return image?.thumbnails?.length > 0 ? image?.thumbnails?.filter(t => t.format === 'w135wp')[0].url : ''
  }

  public async redirect($event: { selectedDepot: number; selectedPurchaseOrderStrategy: PurchaseOrderStrategy }) {
    let productsToImport = this.checkedProducts()
    if (this.areAllProductsChecked()) {
      // Phase 1: Snapshot all matching product IDs (and sellPrice if needed) with stable sort by id ASC
      const snapshot: Array<Product> = []
      const snapshotSchema: any = {
        id: null,
        isBundle: null,
        sellPrice: null,
        purchasePrice: null,
        name: null,
        fullName: null,
        purchaseCurrency: {
          id: null,
          name: null,
        },
        // currency: null,
        quantities: {
          depot: {
            id: null,
            name: null,
          },
          quantityStock: null,
          quantityReservation: null,
          quantityAvailable: null,
          quantityPurchase: null,
        },
        metrics: {
          depot: {
            id: null,
          },
          quantityMinimum: null,
          quantityOptimum: null,
          quantityMaximum: null,
          quantityStock: null,
        },
      }
      let page = 1
      let endPage: number

      do {
        const { items, paginator } = await this.core.services.product.getList(
          {
            page,
            sort: 'id',
            direction: 'asc',
            filters: this.submittedFilters.snapshot()?.request ?? {},
          },
          snapshotSchema
        )
        if (page === 1) {
          endPage = paginator.endPage
        }
        // collect snapshot of ids (+sellPrice if requested)
        for (const p of items as any[]) {
          snapshot.push(p)
        }
        page++
      } while (page <= endPage)

      productsToImport = snapshot
    }

    const partitioned = (productsToImport ?? []).reduce(
      (acc, p) => {
        if (p?.isBundle) acc.virtuals.push(p)
        else acc.nonVirtuals.push(p)
        return acc
      },
      { virtuals: [] as Product[], nonVirtuals: [] as Product[] }
    )
    const virtuals = partitioned.virtuals
    const nonVirtuals = partitioned.nonVirtuals
    if (virtuals.length > 0) {
      // todo jen vyhodit hlasku
      productsToImport = nonVirtuals
      this.notificationService.warning(
        this.translateService.instant('modal-product-purchase.virtual-products-skipped.error')
      )
    }
    // Ensure we still have at least one non-virtual product to proceed
    if (!productsToImport || productsToImport.length === 0) {
      alert(this.translateService.instant('modal-product-purchase.non-non-virtual-product.to.import.error'))
      this.loading = false
      this.modalproductPurchase.stopProcess()
      return
    }
    const requiredQuantityLevelField = this.getRequiredQuantityLevelField($event.selectedPurchaseOrderStrategy)
    const productsWithQuantityLevel: Product[] = []
    const excludedProductsMissingQuantityLevel: ExcludedPurchaseOrderProduct[] = []

    for (const prod of productsToImport ?? []) {
      if (
        requiredQuantityLevelField &&
        !this.hasRequiredQuantityLevel(prod, requiredQuantityLevelField, $event.selectedDepot)
      ) {
        excludedProductsMissingQuantityLevel.push({
          id: prod.id,
          fullName: prod.fullName || prod.name || String(prod.id),
          code: prod.code,
          ean: prod.ean,
          missingLevel: this.getQuantityLevelLabel(requiredQuantityLevelField),
        })
        continue
      }

      productsWithQuantityLevel.push(prod)
    }

    const product = productsWithQuantityLevel.map(prod => {
      const calculatedQuantity = this.calculateQuantity(
        prod,
        $event.selectedPurchaseOrderStrategy,
        $event.selectedDepot
      )
      return (({ code, ean, fullName, mainImage, note, id, purchaseCurrency, purchasePrice }) => ({
        code,
        ean,
        fullName,
        mainImage,
        note,
        product: id,
        currency: purchaseCurrency,
        purchasePrice,
        quantity: calculatedQuantity,
        purchasePriceAll: calculatedQuantity * purchasePrice,
      }))(prod)
    })
    // todo    }).filter(Boolean)

    this.router.navigate(['/purchase-order'], {
      state: {
        selectedProducts: product,
        depot: $event.selectedDepot,
        excludedProductsMissingQuantityLevel,
      },
    })
  }

  private getRequiredQuantityLevelField(purchaseOrderStrategy: PurchaseOrderStrategy): QuantityLevelField | null {
    switch (purchaseOrderStrategy) {
      case 'less_than_minimum':
        return 'quantityMinimum'
      case 'less_than_optimum':
        return 'quantityOptimum'
      case 'more_than_optimum':
        return 'quantityMaximum'
      case 'missing_from_orders':
        return null
    }
  }

  private getQuantityLevelLabel(fieldName: QuantityLevelField): string {
    switch (fieldName) {
      case 'quantityMinimum':
        return 'product.quantity.levels.minimal'
      case 'quantityOptimum':
        return 'product.quantity.levels.optimal'
      case 'quantityMaximum':
        return 'product.quantity.levels.maximal'
    }
  }

  private hasRequiredQuantityLevel(product: Product, fieldName: QuantityLevelField, selectedDepot: number): boolean {
    const metric = product.metrics.find(item => item.depot?.id == selectedDepot)
    const quantityLevel = Number(metric?.[fieldName] ?? 0)
    return quantityLevel > 0
  }

  private calculateQuantity(
    product: Product,
    purchaseOrderStrategy: PurchaseOrderStrategy,
    selectedDepot: number
  ): number {
    const quantityItem = product.quantities.find(item => item.depot?.id == selectedDepot)
    let tempQuantity
    if (purchaseOrderStrategy === 'missing_from_orders') {
      tempQuantity =
        (quantityItem?.quantityReservation || 0) -
        (quantityItem?.quantityStock || 0) -
        (quantityItem?.quantityPurchase || 0)
    } else {
      const fieldName = this.getRequiredQuantityLevelField(purchaseOrderStrategy)
      const metric = product.metrics.find(item => item.depot?.id == selectedDepot)
      const quantityMetric = fieldName && metric ? metric[fieldName] || 0 : 0
      tempQuantity = quantityMetric - (quantityItem?.quantityAvailable || 0) - (quantityItem?.quantityPurchase || 0)
    }
    return tempQuantity > 0 ? tempQuantity : 0
  }

  private hasPositionFilter(requestFilters: Record<string, any>): boolean {
    return !!(requestFilters?.position1 || requestFilters?.position2 || requestFilters?.position3)
  }

  setSchema(requestFilters: Record<string, any> = {}): Partial<Product> {
    const schema: Partial<Product | any> = {
      id: null,
      name: null,
      fullName: null,
      code: null,
      ean: null,
      enabled: null,
      purchasePrice: null,
      purchaseCurrency: {
        id: null,
        name: null,
      },
      quantities: {
        depot: {
          id: null,
          name: null,
        },
        quantityStock: null,
        quantityReservation: null,
        quantityAvailable: null,
        quantityPurchase: null,
      },
      metrics: {
        depot: {
          id: null,
        },
        quantityMinimum: null,
        quantityOptimum: null,
        quantityMaximum: null,
        quantityStock: null,
      },
    }
    if (this.isColumnSelected('name')) {
      schema.mainImage = {
        id: null,
        text: null,
        originalFilename: null,
        main: null,
        mimeType: null,
        size: null,
        url: null,
        thumbnails: {
          format: null,
          mimeType: null,
          url: null,
        },
      }
    }
    if (this.isColumnSelected('availability')) {
      schema.isBundle = null
      schema.quantityAvailable = null
      schema.quantityReservation = null
      schema.quantityStock = null
      schema.quantityPurchase = null
      // schema.quantities = {
      //   depot: { id: null },
      //   quantityAvailable: null,
      //   quantityReservation: null,
      //   quantityStock: null,
      //   quantityPurchase: null,
      // }
    }
    if (this.isColumnSelected('availability') && this.hasPositionFilter(requestFilters)) {
      // The stock semaphore sums only product depots on the filtered position, so load them with the list.
      schema.productDepots = {
        id: null,
        depot: { id: null, name: null, unavailablesUrl: null },
        supplier: { id: null, name: null },
        purchasePrice: null,
        quantityStock: null,
        quantityReservation: null,
        quantityAvailable: null,
        inventoryQuantityStock: null,
        batch: null,
        expirationDate: null,
        position: null,
        position1: null,
        position2: null,
        position3: null,
        created: null,
        updated: null,
      }
    }
    if (this.isColumnSelected('tags')) {
      schema.tags = {
        id: null,
        name: null,
        color: null,
      }
    }
    if (this.isColumnSelected('sellPrice')) {
      schema.sellPrice = null
      schema.actualSellPrice = null
      schema.purchaseCurrency = {
        id: null,
        name: null,
        ratio: null,
      }
    }
    if (this.isColumnSelected('weight')) {
      schema.weight = null
    }
    if (this.isColumnSelected('created')) {
      schema.created = null
    }
    if (
      this.filters().listType === 'all' ||
      this.filters().listType === 'parents' ||
      this.filters().listType === 'parents_with_children'
    ) {
      schema.children = {
        id: null,
        name: null,
        fullName: null,
        code: null,
        ean: null,
        enabled: null,
        mainImage: {
          url: null,
          thumbnails: { format: null, url: null },
        },
        sellPrice: null,
        actualSellPrice: null,
        quantityAvailable: null,
        quantityReservation: null,
        quantityStock: null,
        quantityPurchase: null,
      }
      if (this.isColumnSelected('weight')) {
        schema.children.weight = null
      }
    }
    return schema
  }

  allProductsCheckedChanged(areAllProductsChecked: boolean) {
    this.areAllProductsChecked.set(areAllProductsChecked)
    this.allProductsChecked(areAllProductsChecked)
  }

  productWasChecked(productId: number, checked: boolean) {
    const mapSignal = this.checkedMap
    if (!mapSignal) return // ignore in pages where checked isn't used
    if (!checked) {
      this.areAllProductsChecked.set(false)
    }
    mapSignal.update(map => ({
      ...map,
      [productId]: checked,
    }))
  }

  setFilters(property: string, value: any, dateLeftRight?: string) {
    if (dateLeftRight) {
      this.filters.set({
        ...this.filters(),
        [property]: {
          ...this.filters()[property],
          [dateLeftRight]: value,
        },
      })
    } else {
      this.filters.set({
        ...this.filters(),
        [property]: value,
      })
    }
  }

  toggleCollapse(productId: number): void {
    this.collapsedStates[productId] = !this.collapsedStates[productId]
  }

  isCollapsed(productId: number): boolean {
    return this.collapsedStates[productId] || false
  }

  protected readonly DEFAULT_FILTERS = DEFAULT_FILTERS
}
